import { useToast } from '@/use/toast';
import { STRIPE_PUBLISHED_KEY } from '@/lib/env';
import { useI18n } from '@/i18n';
import { logError } from '@/lib/logError';
import { useBillingStore } from '@/stores/billing';
import { usePaymentStore } from '@/stores/payment';
import { useUserStore } from '@/stores/user';
import type { BillingAddress } from '@/types/billing';
import type {
  Stripe,
  StripeAddressElement,
  StripeElements,
  StripeExpressCheckoutElement,
  StripeExpressCheckoutElementConfirmEvent,
  StripePaymentElementOptions,
} from '@stripe/stripe-js';
import { computed, onUnmounted, ref, type Ref } from 'vue';

/** Trim and return undefined for empty/non-string values, for Stripe billingDetails defaults. */
function billingString(value: string | null | undefined): string | undefined {
  if (value == null || typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

interface StripeAddressLike {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
}

/**
 * Normalize a Stripe address (Address Element, wallet or the customer on file) to our
 * payload, or null if it has no street or country. Which other fields an address needs
 * depends on the country: Hong Kong has no postcode, for one (DABBLE-ACCOUNTS-2-4H). The
 * Address Element's `complete` already applies those rules, so don't add our own.
 */
function toBillingAddress(a: StripeAddressLike | null | undefined): BillingAddress | null {
  if (!a?.line1 || !a.country) return null;
  return {
    line1: a.line1,
    country: a.country,
    ...(a.line2 ? { line2: a.line2 } : {}),
    ...(a.city ? { city: a.city } : {}),
    ...(a.state ? { state: a.state } : {}),
    ...(a.postal_code ? { postal_code: a.postal_code } : {}),
  };
}

/**
 * Owns the Stripe Elements lifecycle for the checkout flow.
 *
 *  - `init()` loads Stripe, creates a SetupIntent, and mounts the payment +
 *    express-checkout elements into the provided container refs.
 *  - `submit()` validates the payment element, confirms the setup, then calls
 *    `onConfirmed` (for the Subscribe button flow).
 *  - The express-checkout `confirm` event automatically runs the same
 *    confirm-then-`onConfirmed` flow (express has its own UI for validation).
 *  - `isInitializing` / `isPaymentComplete` / `isSubmitting` are exposed for
 *    the host to drive button state.
 *  - Tears down via `paymentStore.teardownPaymentForm()` on component unmount.
 */
export function useStripeCheckout(options: {
  paymentContainer: Ref<HTMLDivElement | null>;
  expressContainer: Ref<HTMLDivElement | null>;
  addressContainer: Ref<HTMLDivElement | null>;
  getReturnUrl: () => string;
  onConfirmed: () => Promise<void>;
  // Called after the billing address is (re)saved to the Stripe customer, so the
  // host can re-preview the upcoming invoice and refresh the tax line.
  onAddressSaved?: () => void | Promise<void>;
}) {
  const billingStore = useBillingStore();
  const paymentStore = usePaymentStore();
  const userStore = useUserStore();
  const { showToast } = useToast();
  const { t } = useI18n();

  const isInitializing = ref(true);
  const isPaymentComplete = ref(false);
  const isSubmitting = ref(false);
  // True when the customer already has an address on file, so it's shown as a
  // collapsed one-line summary until the user clicks "Change".
  const isAddressCollapsed = ref(false);
  const alive = ref(true);

  // expressEl is owned here (not the paymentStore) because nothing outside this
  // composable needs to read it. Tracked so we can .destroy() it on unmount —
  // otherwise the DOM node and its 'confirm' listener leak across nav cycles.
  let expressEl: StripeExpressCheckoutElement | null = null;
  // Billing address element + its own Elements group (no customer session, so no
  // saved-address "Update/Cancel" UI). Owned here like expressEl. Created lazily:
  // mounted on init when there's no usable address on file, otherwise only when
  // the user reveals it via "Change". `addressStripe`/`addressClientSecret` are
  // captured in init() so the lazy mount can build the group later.
  let addressEl: StripeAddressElement | null = null;
  let addressElements: StripeElements | null = null;
  let addressStripe: Stripe | null = null;
  let addressClientSecret = '';
  let addressSaveTimer: ReturnType<typeof setTimeout> | null = null;
  let lastSavedAddressKey = '';
  // The address this session last saved to the Stripe customer. `saveBillingAddress`
  // also writes it onto `billingStore.customer`, but only when a customer has been
  // fetched; this doesn't need one, so a checkout that never loaded an overview still
  // finds the address after the form is rebuilt (DAB-1407).
  const savedAddress = ref<BillingAddress | null>(null);

  // The complete address on file (see toBillingAddress), or undefined.
  // Used both to prefill/collapse the form and as the confirm billing details
  // when the form stays collapsed.
  function existingBillingAddress(): BillingAddress | undefined {
    return toBillingAddress(billingStore.customer?.address) ?? savedAddress.value ?? undefined;
  }

  // One-line summary of the address on file, for the host's collapsed view. Built from
  // the same resolver the collapse decision uses, so the two can't disagree.
  const billingAddressSummary = computed(() => {
    const a = existingBillingAddress();
    if (!a) return '';
    return [a.line1, a.line2, a.city, a.state, a.postal_code, a.country].filter(Boolean).join(', ');
  });

  // Build + mount the full Address Element into its container. Idempotent.
  function mountAddressElement(): void {
    if (addressEl || !addressStripe || !options.addressContainer.value || !alive.value) return;
    const billingName = billingString(billingStore.customer?.name) ?? billingString(userStore.user.name);
    const existing = existingBillingAddress();
    // Treat a prefilled address as already-saved so its initial 'change' doesn't
    // re-save or re-preview an address the customer hasn't touched.
    if (existing) lastSavedAddressKey = JSON.stringify(existing);
    addressElements = addressStripe.elements({
      clientSecret: addressClientSecret,
      loader: 'auto',
      appearance: { theme: 'stripe' },
    });
    addressEl = addressElements.create('address', {
      mode: 'billing',
      autocomplete: { mode: 'automatic' },
      fields: { phone: 'never' },
      defaultValues: {
        ...(billingName ? { name: billingName } : {}),
        ...(existing ? { address: existing } : {}),
      },
    });
    // Live tax: once the address reads as complete, save it and re-preview.
    addressEl.on('change', (event) => {
      if (!event.complete) return;
      const address = toBillingAddress(event.value.address);
      if (address) scheduleBillingAddressSave(address);
    });
    addressEl.mount(options.addressContainer.value);
  }

  // Swap the collapsed on-file summary for the editable Address Element.
  function revealAddress(): void {
    isAddressCollapsed.value = false;
    mountAddressElement();
  }

  // Persist the address to the Stripe customer (for tax), then let the host
  // re-preview the invoice. Deduped so repeat 'change' events don't re-save.
  async function persistBillingAddress(address: BillingAddress): Promise<void> {
    const key = JSON.stringify(address);
    if (key === lastSavedAddressKey) return;
    try {
      // saveBillingAddress reports failure by returning false, not by throwing. Marking
      // the key saved on a failed POST would make the dedupe below swallow every retry
      // of that address — including the customer retyping it on a rebuilt form.
      if (!(await billingStore.saveBillingAddress(address))) return;
      lastSavedAddressKey = key;
      savedAddress.value = address;
      if (alive.value) await options.onAddressSaved?.();
    } catch (err) {
      logError('Failed to save billing address:', err);
    }
  }

  // Debounce live saves so we don't fire on every keystroke once the address
  // first reads as complete.
  function scheduleBillingAddressSave(address: BillingAddress): void {
    if (JSON.stringify(address) === lastSavedAddressKey) return;
    if (addressSaveTimer) clearTimeout(addressSaveTimer);
    addressSaveTimer = setTimeout(() => {
      addressSaveTimer = null;
      void persistBillingAddress(address);
    }, 600);
  }

  // Resolve the address to send as confirm billing details (the card widget no
  // longer collects one). Prefers the mounted Address Element (saving any edit);
  // falls back to the address already on file when the form stayed collapsed.
  // Returns undefined only on the express/wallet path (no card address needed).
  async function captureBillingAddress(): Promise<{ address: BillingAddress } | undefined> {
    if (addressEl) {
      try {
        const { complete, value } = await addressEl.getValue();
        if (complete) {
          const address = toBillingAddress(value.address);
          if (address) {
            await persistBillingAddress(address);
            return { address };
          }
        }
      } catch (err) {
        logError('Failed to save billing address:', err);
      }
      return undefined;
    }
    const onFile = existingBillingAddress();
    return onFile ? { address: onFile } : undefined;
  }

  // Save the wallet-provided billing address (the express path has no Address Element).
  async function saveWalletAddress(event: StripeExpressCheckoutElementConfirmEvent): Promise<void> {
    const address = toBillingAddress(event.billingDetails?.address);
    if (!address) return;
    try {
      await billingStore.saveBillingAddress(address);
    } catch (err) {
      logError('Failed to save wallet billing address:', err);
    }
  }

  // Once Stripe has confirmed the SetupIntent it is spent — Stripe answers any second
  // confirm with `setup_intent_unexpected_state`, no matter what the customer does. So a
  // failure after that point (the default-method request, activate declined, the 3DS
  // challenge abandoned, a network blip) must rebuild the form on a fresh SetupIntent, or
  // every retry on the page is dead until a reload (DAB-1407). The store marks the intent
  // spent the moment Stripe accepts it (`setupIntentConsumed`), so a failure inside
  // confirmSetup's own follow-up request counts too. The card is already saved on the
  // customer, so the rebuilt form offers it back as a saved method.
  async function runConfirm(): Promise<void> {
    if (addressSaveTimer) {
      clearTimeout(addressSaveTimer);
      addressSaveTimer = null;
    }
    const billingDetails = await captureBillingAddress();
    await paymentStore.confirmSetup(options.getReturnUrl(), undefined, billingDetails);
    if (alive.value) await options.onConfirmed();
  }

  // What the customer reads when the step *after* saving the card fails. The server
  // reports a Stripe decline or a checkout state as its bare code (`card_declined`,
  // `PAYMENT_INCOMPLETE`); the ones with a translation get it, the rest keep the code
  // beside the generic line so a support screenshot still says what happened (DAB-957).
  function describeActivationError(err: unknown): string {
    const generic = t('payment_error_unexpected');
    const code = err instanceof Error ? err.message : '';
    if (!code) return generic;
    // Ask the map whether it knows the code, rather than inferring it by comparing the
    // translated result against the generic string — two keys can render the same copy.
    return billingStore.translateCheckoutError(code) ?? `${generic} (${code})`;
  }

  // Runs after any failed submit. Surfaces the reason in the form's Alert (the
  // payment store's own failures are already there) and, if the SetupIntent was
  // spent, tears the Elements down and mounts a fresh set so the retry can work.
  async function recoverFromFailure(err: unknown): Promise<void> {
    if (!paymentStore.error) paymentStore.error = describeActivationError(err);
    if (!paymentStore.setupIntentConsumed || !alive.value) return;
    const error = paymentStore.error;
    teardownElements();
    const rebuilt = await init();
    if (!alive.value) return;
    // init() clears the store's error on entry, so put the customer's reason back. If
    // the rebuild itself failed and left a message of its own, that one matters more —
    // it's the reason there is no form to retry with.
    if (rebuilt || !paymentStore.error) paymentStore.error = error;
  }

  // Destroy every Stripe element this composable mounted. Idempotent; shared by the
  // unmount path and the post-failure rebuild.
  function teardownElements(): void {
    if (addressSaveTimer) {
      clearTimeout(addressSaveTimer);
      addressSaveTimer = null;
    }
    try {
      expressEl?.destroy();
    } catch {
      /* ignore — Stripe sometimes throws if already destroyed */
    }
    expressEl = null;
    try {
      addressEl?.destroy();
    } catch {
      /* ignore — Stripe sometimes throws if already destroyed */
    }
    addressEl = null;
    addressElements = null;
    addressStripe = null;
    paymentStore.teardownPaymentForm();
  }

  /** Returns true when the form was rebuilt; false when it could not be. */
  async function init(): Promise<boolean> {
    paymentStore.clearError();
    isInitializing.value = true;
    isPaymentComplete.value = false;
    try {
      const stripe = await paymentStore.initializeStripe(STRIPE_PUBLISHED_KEY);
      if (!alive.value) return false;

      const clientSecret = await paymentStore.createSetupIntent();
      if (!alive.value) return false;

      const customerSessionClientSecret = paymentStore.customerSessionClientSecret || undefined;
      const elements = stripe.elements({
        clientSecret,
        ...(customerSessionClientSecret ? { customerSessionClientSecret } : {}),
        loader: 'auto',
        appearance: { theme: 'stripe' },
      });
      paymentStore.setElements(elements);

      const billingName = billingString(billingStore.customer?.name) ?? billingString(userStore.user.name);
      const billingEmail = billingString(billingStore.customer?.email) ?? billingString(userStore.user.email);

      const paymentOptions: StripePaymentElementOptions = {
        defaultValues: {
          billingDetails: {
            ...(billingName ? { name: billingName } : {}),
            ...(billingEmail ? { email: billingEmail } : {}),
          },
        },
        // The Address Element below is the single billing-address source, so hide
        // the card widget's country/postal fields (avoids duplicate entry). The
        // address is supplied at confirm via payment_method_data.billing_details.
        fields: { billingDetails: { address: 'never' } },
        // Wallets live in the Express Checkout Element above; suppress them here so
        // Apple/Google Pay aren't duplicated inside the Payment Element accordion.
        wallets: { applePay: 'never', googlePay: 'never' },
      };

      const paymentEl = elements.create('payment', paymentOptions);
      paymentStore.setPaymentElement(paymentEl);

      paymentEl.on('change', (event) => {
        isPaymentComplete.value = event.complete;
      });

      if (options.paymentContainer.value && alive.value) {
        paymentEl.mount(options.paymentContainer.value);
      }

      // Billing address for Stripe Tax jurisdiction. Lives in its OWN Elements
      // group (no customer session) so Stripe renders a plain editable form, not
      // the saved-address "Update/Cancel" UI. When a complete address is already
      // on file we skip the form entirely and show a collapsed summary (tax is
      // already correct from that address); otherwise we mount the form now.
      addressStripe = stripe;
      addressClientSecret = clientSecret;
      // Set explicitly on both branches: init() is re-entered by recoverFromFailure, so
      // a flag left over from the previous form would render the collapsed summary and
      // the Address Element at once.
      if (existingBillingAddress()) {
        isAddressCollapsed.value = true;
      } else {
        isAddressCollapsed.value = false;
        mountAddressElement();
      }

      // Express checkout uses its OWN elements group so the card group's Address
      // Element (which validates on every confirm in its group) can't block a wallet
      // payment. The billing address comes from the wallet instead.
      const expressElements = stripe.elements({ clientSecret, loader: 'auto', appearance: { theme: 'stripe' } });
      expressEl = expressElements.create('expressCheckout', {
        paymentMethods: { applePay: 'auto', googlePay: 'auto', link: 'auto', paypal: 'auto' },
        billingAddressRequired: true,
        // Stack every available wallet full-width instead of collapsing the
        // extras behind a "See more" overflow menu.
        layout: { maxColumns: 1, overflow: 'never' },
      });
      const thisExpressEl = expressEl;
      expressEl.on('confirm', async (event) => {
        // A wallet can report back after its element was destroyed — the page left,
        // or a rebuild replaced it (DABBLE-ACCOUNTS-2-4H: 5 min after checkout had
        // already succeeded). Its group has nothing mounted any more, so confirming
        // throws an IntegrationError, and saving its address would overwrite the
        // customer's. Close the wallet sheet and do nothing.
        if (!alive.value || expressEl !== thisExpressEl) {
          try {
            event.paymentFailed();
          } catch {
            /* ignore — the element is already destroyed */
          }
          return;
        }
        if (isSubmitting.value) return;
        isSubmitting.value = true;
        paymentStore.clearError();
        try {
          await saveWalletAddress(event);
          await paymentStore.confirmSetup(options.getReturnUrl(), expressElements);
          if (alive.value) await options.onConfirmed();
        } catch (err) {
          await recoverFromFailure(err);
        } finally {
          isSubmitting.value = false;
        }
      });
      if (options.expressContainer.value && alive.value) {
        expressEl.mount(options.expressContainer.value);
      }
      return true;
    } catch (err) {
      logError('Failed to initialize payment form:', err);
      // Same guard the confirm catchers use. Now that the Alert renders its body,
      // a createSetupIntent failure already shows the customer a message — without
      // this they'd get the Alert and a toast saying two different things.
      if (!paymentStore.error) showToast('danger', t('payment_error_unexpected'));
      return false;
    } finally {
      isInitializing.value = false;
    }
  }

  async function submit(): Promise<void> {
    if (isSubmitting.value) return;
    const elements = paymentStore.elements;
    if (!elements) return;

    isSubmitting.value = true;
    paymentStore.clearError();
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        if (submitError.type === 'card_error' || submitError.type === 'validation_error') {
          showToast('danger', submitError.message ?? t('payment_error_unexpected'));
        } else {
          showToast('danger', t('payment_error_unexpected'));
        }
        return;
      }
      // The address lives in its own Elements group, so validate it separately
      // (surfaces inline field errors) before confirming.
      if (addressElements) {
        const { error: addressError } = await addressElements.submit();
        if (addressError) {
          showToast('danger', addressError.message ?? t('payment_error_unexpected'));
          return;
        }
      }
      await runConfirm();
    } catch (err) {
      await recoverFromFailure(err);
    } finally {
      isSubmitting.value = false;
    }
  }

  onUnmounted(() => {
    alive.value = false;
    teardownElements();
  });

  return {
    isInitializing,
    isPaymentComplete,
    isSubmitting,
    isAddressCollapsed,
    billingAddressSummary,
    revealAddress,
    init,
    submit,
  };
}
